import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Mailcheck from 'mailcheck';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getNames } from 'country-list';
import './Contact.css';
import useScrollToTop from './ScrollToTop';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import validator from 'validator'; //  Added email validator

const Contact = () => {
  const location = useLocation();
  const navigate = useNavigate(); //  To redirect to thank you page

  // Form state
  const [email, setEmail] = useState('');
  const [emailSuggestion, setEmailSuggestion] = useState('');
  const [source, setSource] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [selectedGuide, setSelectedGuide] = useState('');
  const [travelDate, setTravelDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedAccommodation, setSelectedAccommodation] = useState('');
  const [preferredContact, setPreferredContact] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false); //  Loading state

  useEffect(() => {
    const sourceParam = new URLSearchParams(location.search).get('source');
    if (sourceParam) setSource(sourceParam);
  }, [location.search]);

  useScrollToTop();

  const countries = getNames().map((country) => ({ value: country, label: country }));

  const activities = [
    'Maasai Mara Game Reserve',
    'Lake Naivasha',
    'Lake Nakuru',
    'Amboseli National Reserve',
    'Osiligi Medical Foundation',
    'Giraffe Centre',
  ];

  const guides =[
    'English',
    'French',
    'Spanish',
    'Japanese',
    'Italian',
    'German',
  ]

  const safariPackages = [
    'RHINO & BIG FIVE SAFARI',
    'HEALTH VOLUNTEER SAFARI',
    'SUMMER WILDBEAST SAFARI',
    'FAMILY SAFARIS',
    'FLYING SAFARIS',
  ];

  const accommodations = [
    'On Budget Tented Camp',
    'Luxury Tented Camp',
    'Premium Luxury Camp',
  ];

  const contactMethods = ['WhatsApp', 'Email', 'Phone Call'];

  const budgetOptions = [
    'Below $500', '$500 – $1000', '$1000 – $2000', '$2000 – $3000',
    '$3000 – $5000', '$5000 - $10000', 'Above 10000',
  ];

  const handleActivityChange = (activity) => {
    setSelectedActivities((prev) =>
      prev.includes(activity)
        ? prev.filter((item) => item !== activity)
        : [...prev, activity]
    );
  };

  const showSuccessToast = () => {
    toast.success('Message sent successfully! Redirecting...', {
      position: 'top-right',
      autoClose: 2000,
    });
  };
const handleSubmit = async (e) => {
  e.preventDefault();
  const form = e.target;
  const elements = form.elements;

  const submittedEmail = elements.email.value;

  //  Email validation
  if (!validator.isEmail(submittedEmail)) {
    toast.error('Please enter a valid email address.');
    return;
  }

  //  Date logic validation
  if (travelDate && endDate && endDate <= travelDate) {
    toast.error('End date must be after start date.');
    return;
  }

  if (selectedPackage.length === 0) {
    toast.error('Please select at least one safari package.');
    return;
  }

  const formData = {
    name: elements.name.value,
    phone: elements.phone.value,
    email: submittedEmail,
    country: selectedCountry?.value || '',
    places: selectedActivities,
    safariPackage: selectedPackage,
    guide: selectedGuide,
    travelDate: travelDate?.toISOString(),
    endDate: endDate?.toISOString(),
    nights: elements.nights?.value || '',
    accommodation: selectedAccommodation,
    adults: elements.adults.value,
    children: elements.children.value,
    budget: elements.budget.value,
    currency: elements.currency.value,
    preferredContact,
    message: elements.message.value,
    source,
  };

  setIsSubmitting(true); // Set loading state

  try {
    const res = await fetch('/backend/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });

    if (res.ok) {
      showSuccessToast();
      form.reset();
      setSelectedCountry(null);
      setSelectedActivities([]);
      setTravelDate(null);
      setEndDate(null);
      setSelectedAccommodation('');
      setPreferredContact('');
      setEmail(''); // Clear email field

      setTimeout(() => {
        navigate('/thankyou'); //  Redirect on success
      }, 2000);
    } else {
      const errorData = await res.json();
      toast.error(
        errorData.error || errorData.message || 'Something went wrong. Please try again.'
      );
    }
  } catch (error) {
    toast.error('Server error. Please try again later.');
    console.error('Submission error:', error); // Debug log
  } finally {
    setIsSubmitting(false); // Reset loading state
  }
};

  return (
    <section className="contact-section" id="contact">
      <div className="contact-container">
        <ToastContainer />
        <h2 className="contact-heading">Enquiry Form</h2>
        <p className="contact-subtext">
          Have questions or need assistance planning your safari? Our team is here to help.
        </p>

        {/* Loading message */}
        {isSubmitting && <p className="submitting-message" aria-live="polite">Form is being submitted...</p>}

        <form className="contact-form" onSubmit={handleSubmit} aria-label="Safari Enquiry Form">
          {/* Accessible inputs with proper labels and names */}
          <div>
            <label htmlFor="name">Your name:</label>
            <input type="text" id="name" name="name" required />
          </div>

          <div>
            <label htmlFor="phone">Your contact number:</label>
            <input type="tel" id="phone" name="phone" required />
          </div>

          <div>
            <label htmlFor="email">Your email address:</label>
            <input
  type="email"
  id="email"
  name="email"
  required
  aria-describedby="emailHelp"
  value={email}
  onChange={(e) => {
    const input = e.target.value;
    setEmail(input);

    // Run mailcheck on input change
    Mailcheck.run({
      email: input,
      suggested: (suggest) => setEmailSuggestion(suggest.full),
      empty: () => setEmailSuggestion(''),
    });
  }}
/>

{emailSuggestion && (
  <p className="email-suggestion" style={{ fontSize: '0.9rem', color: '#444' }}>
    Did you mean{' '}
    <button
      type="button"
      onClick={() => {
        setEmail(emailSuggestion);
        setEmailSuggestion('');
      }}
      style={{
        background: 'none',
        color: 'blue',
        textDecoration: 'underline',
        border: 'none',
        cursor: 'pointer',
      }}
    >
      {emailSuggestion}
    </button>
    ?
  </p>
)}

           
          </div>

          <div>
            <label>Select your country:</label>
            <Select
              options={countries}
              value={selectedCountry}
              onChange={setSelectedCountry}
              placeholder="Choose country"
              isClearable
              className="country-dropdown"
              aria-label="Select your country"
            />
          </div>

          <div className="full-width">
            <fieldset>
              <legend>Select the Place(s) You Would Like to Visit:</legend>
              <div className="checkbox-group">
                {activities.map((activity) => (
                  <label key={activity}>
                    <input
                      type="checkbox"
                      value={activity}
                      checked={selectedActivities.includes(activity)}
                      onChange={() => handleActivityChange(activity)}
                    />
                    {activity}
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          <div>
            <label htmlFor="travelDate">Preferred travel date:</label>
            <DatePicker
              selected={travelDate}
              onChange={(date) => setTravelDate(date)}
              placeholderText="Select a date"
              dateFormat="dd MMMM yyyy"
              minDate={new Date()}
              className="date-picker"
              required
              id="travelDate"
            />
          </div>

          <div>
            <label htmlFor="endDate">Safari End date:</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              placeholderText="Select a date"
              dateFormat="dd MMMM yyyy"
              minDate={travelDate || new Date()}
              className="date-picker"
              required
              id="endDate"
            />
          </div>

          <div className="full-width">
            <fieldset>
              <legend>Select Safari Package:</legend>
              <div className="checkbox-group">
                {safariPackages.map((pkg) => (
                  <label key={pkg}>
                    <input
  type="radio"
  name="safariPackage"
  value={pkg}
  checked={selectedPackage === pkg}
  onChange={(e) => setSelectedPackage(e.target.value)}
/>

                    {pkg}
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

         

          <div className="full-width">
            <fieldset>
              <legend>Select Your Preferred Accommodation:</legend>
              <div className="radio-group">
                {accommodations.map((acc) => (
                  <label key={acc}>
                    <input
                      type="radio"
                      name="accommodation"
                      value={acc}
                      checked={selectedAccommodation === acc}
                      onChange={() => setSelectedAccommodation(acc)}
                    />
                    {acc}
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          <div>
            <label htmlFor="adults">Number of adults:</label>
            <input type="number" name="adults" id="adults" min={1} required />
          </div>

          <div>
            <label htmlFor="children">Number of children(U 12):</label>
            <input type="number" name="children" id="children" min={0} placeholder="0 if none" required />
          </div>

          <div>
            <label htmlFor="budget">Choose a budget range (Per person):</label>
            <select name="budget" id="budget" required defaultValue="">
              <option value="" disabled>Select your budget range</option>
              {budgetOptions.map((budget, index) => (
                <option key={index} value={budget}>
                  {budget}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="currency">Select Your Currency:</label>
            <select name="currency" id="currency" required defaultValue="">
              <option value="" disabled>Select your currency</option>
              <option value="USD">USD</option>
              <option value="KES">KES</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="Other">Other</option>
            </select>
          </div>

<div className="full-width">
            <fieldset>
              <legend>Preferred Language for Guide</legend>
              <div className="checkbox-group">
                {guides.map((guide) => (
                  <label key={guide}>
                    <input
  type="radio"
  name="PreferredGuide"
  value={guide}
  checked={selectedGuide === guide}
  onChange={(e) => setSelectedGuide(e.target.value)}
/>

                    {guide}
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          <div className="full-width">
            <fieldset>
              <legend>How would you like to be contacted:</legend>
              <div className="radio-group">
                {contactMethods.map((method) => (
                  <label key={method}>
                    <input
                      type="radio"
                      name="contact"
                      value={method}
                      checked={preferredContact === method}
                      onChange={() => setPreferredContact(method)}
                    />
                    {method}
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          <div className="full-width">
            <label htmlFor="message">Your message:</label>
            <textarea name="message" id="message" placeholder="Your Message" rows="5" required></textarea>
          </div>

          <div className="full-width">
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Send Message'}
            </button>
          </div>
        </form>

      
      
        <div className="customize-button-wrapper">
            <p>
          Prefer something uniquely your own? Design your own safari package—tailored to your travel style, budget, and dream destinations.
        </p>
          <Link to="/customizesafari" className="customize-button" aria-label="Customize your safari">
            Customize Your Safari
          </Link>
        </div>
        <hr style={{ border: '2px solid #ccc', margin: '15px 0' }} />
      </div>
    </section>
  );
};

export default Contact;
